import { clsx } from 'clsx'
import { type ChangeEvent, useEffect, useRef } from 'react'
import toast from 'react-hot-toast'

import { TextAreaField } from '@fv/client-components'
import { useCurrencyOptions } from '@fv/client-core'
import type { Currency } from '@fv/models'
import type { EquipmentType } from '@fv/models/equipment'

import { MAX_ALT_EQUIP } from '@/constants'
import type { QuoteFormLoad } from '@/features/quote-request/types'
import {
  useQuoteFormActions,
  useQuoteFormLocations,
} from '@/features/quote-request/useQuoteFormState'
import type { AccessorialTypeEnum } from '@/hooks/settings/useAccessorialOptions'

import { ValidatedForm } from '../../components/inputs'
import { InputAdornment, InputGroup } from '../../components/inputs/InputGroup'
import { SelectField } from '../../components/inputs/SelectField'
import { ShipperMultiSelect } from '../../components/inputs/ShipperMultiSelect'
import YesNoToggle from '../../components/inputs/YesNoToggle'
import { Pinline } from '../../components/Pinline'
import EquipmentAccessorialConfig from '../../components/shared/EquipmentAccessorialConfig'

type AltEquipmentOption = {
  label: string
  value: EquipmentType
}

type EquipmentConfigProps = {
  accessorialOptions: AccessorialTypeEnum[]
  altEquipmentOptions: AltEquipmentOption[]
  load: QuoteFormLoad
}

export const EquipmentConfig = ({
  accessorialOptions,
  altEquipmentOptions,
  load,
}: EquipmentConfigProps) => {
  const { loadId, isActiveInUI, revalidationDelay, type } = load
  const { setLoadData, validateLoad } = useQuoteFormActions()
  const stops = useQuoteFormLocations()
  const allowedCurrencies = useCurrencyOptions(stops)
  const formRef = useRef<HTMLFormElement>(null)

  // Set `formRef` on mount
  useEffect(() => {
    setLoadData(type, loadId, { formRef: formRef.current })
  }, [loadId, setLoadData, type])

  // Watch for active loads that need to be validated
  useEffect(() => {
    if (!isActiveInUI || revalidationDelay === null) return

    // Need validation to run in a specific order
    setTimeout(() => {
      validateLoad(type, loadId)
    }, revalidationDelay)
  }, [isActiveInUI, loadId, revalidationDelay, type, validateLoad])

  function updateLoad(data: Partial<QuoteFormLoad>) {
    setLoadData(type, loadId, data)
  }

  return (
    <ValidatedForm
      className={clsx(
        '[&>*]:mb-3 [&>*]:relative [&>*]:before:absolute [&>*]:before:w-[14px] [&>*]:before:h-px [&>*]:before:bg-fv-gray-dark [&>*]:before:left-[-14px] [&>*]:before:top-1/2',
        !isActiveInUI && 'pointer-events-none',
      )}
      ref={formRef}
      // Hiding inactive forms so `ref` always available in Provider
      style={{ display: isActiveInUI ? 'block' : 'none' }}
    >
      <InputGroup
        inputType="custom"
        required
        inputProps={{ name: `${loadId}_description` }}
        startContent={
          <InputAdornment
            icon="quote-left"
            iconClass="text-fv-gray-dark mt-[0.7rem] self-start"
            position="start"
          />
        }
      >
        <TextAreaField
          autoFocus={isActiveInUI}
          className="form-control h-auto resize-none"
          name={`${loadId}_description`}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
            updateLoad({ description: e.target.value })
          }}
          placeholder="What are you shipping?"
          required
          rows={2}
          value={load.description}
        />
      </InputGroup>

      <InputGroup
        inputType="amount"
        inputProps={{
          min: 1,
          name: `${loadId}_weight`,
          onChange: (e: ChangeEvent<HTMLInputElement>) => {
            updateLoad({ weight: e.target.value })
          },
          placeholder: 'Weight',
          value: load.weight,
        }}
        required
        startContent={
          <InputAdornment
            icon="weight-hanging"
            iconClass="text-fv-gray-dark"
            position="start"
          />
        }
        endContent={<InputAdornment label="lbs." position="end" />}
      />

      <InputGroup
        inputType="amount"
        inputProps={{
          isPrice: true,
          min: 1,
          name: `${loadId}_declaredValue`,
          onChange: (e: ChangeEvent<HTMLInputElement>) => {
            updateLoad({ declaredValue: e.target.value })
          },
          placeholder: 'Declared value',
          value: load.declaredValue,
        }}
        startContent={
          <InputAdornment
            icon="dollar-sign"
            iconClass="text-fv-gray-dark"
            position="start"
          />
        }
        endContent={
          allowedCurrencies.length > 1 ? (
            <SelectField
              className="w-auto -ml-px"
              name={`${loadId}_declaredValueCurrency`}
              onChange={e =>
                updateLoad({
                  declaredValueCurrency: e.target.value as Currency,
                })
              }
              options={allowedCurrencies}
              value={load.declaredValueCurrency || 'usd'}
            />
          ) : undefined
        }
      />

      <ShipperMultiSelect
        classNames={{
          container: '!mb-6',
        }}
        name="alternateTypes"
        onChange={nextValues => {
          if (nextValues.length > MAX_ALT_EQUIP) {
            toast.error(`Max of ${MAX_ALT_EQUIP} alternate equipment types.`)
            return
          }

          updateLoad({
            alternateTypes: nextValues.map(v => v.value),
          })
        }}
        options={altEquipmentOptions}
        getOptionLabel={o => o.label}
        getOptionValue={o => o.value}
        placeholder="Optional equipment"
        value={altEquipmentOptions.filter(o =>
          load.alternateTypes.includes(o.value),
        )}
      />

      <div className="flex items-center">
        <label htmlFor={`${loadId}_isHazardous`}>Hazardous</label>
        <Pinline className="mx-1" dotted flex />

        <YesNoToggle
          checked={load.isHazardous}
          name={`${loadId}_isHazardous`}
          onChange={e => updateLoad({ isHazardous: e.target.checked })}
        />
      </div>

      <EquipmentAccessorialConfig
        accessorialOptions={accessorialOptions}
        accessorials={load.accessorials}
        id={loadId}
        setAccessorials={updateLoad}
      />
    </ValidatedForm>
  )
}
